export interface CtaStateInput {
    /** Marketplace, in stock, selected variant purchasable. */
    isBuyable: boolean;
    /** Sample can be purchased (independent of main buyability / EcommerceReady). */
    isSampleBuyable: boolean;
    /** Pricing type is ecommercePricing. */
    isMarketplace: boolean;
    /** The product has colours / materials / sizes to pick in the drawer. */
    hasVariantOptions: boolean;
    /**
     * The enquiry CTA label from the professional's settings (e.g. "Request
     * Price / Brochure"). Used for the enquiry-led state instead of hardcoded
     * "Enquire" / "Request Quote". Defaults to a generic label when absent.
     */
    enquiryCTALabel?: string;
    /**
     * The professional's enquiry CTA may show (audience gating). Commerce
     * states lead with Shop Now / Shop Sample either way; the gate decides
     * whether the enquiry keeps a slot beside them.
     */
    enquiryVisible: boolean;
}

/** The primary CTA label. Shop Now / Shop Sample for transactional states; an
 * arbitrary professional label for enquiry-led states. */
export type CtaLabel = string;

export interface CtaState {
    label: CtaLabel;
    /** Enquire / Request Quote — no transaction, an enquiry instead. */
    isEnquiryCTA: boolean;
    /**
     * Enquiry-led with nothing to configure: open the enquiry modal directly.
     * Otherwise the primary CTA opens the purchase drawer (Shop Now / Shop
     * Sample transact there; enquiry-led with variants picks a variant first,
     * which the drawer's Request Quote folds into the enquiry).
     */
    opensEnquiryModalDirectly: boolean;
    /**
     * Render the primary CTA at all. Commerce states always do; enquiry-led
     * ones only when the professional's audience gating lets the enquiry show.
     */
    showPrimaryCta: boolean;
    /**
     * Show the quiet "Samples available to order on ArchiPro" line under the
     * CTA cluster. Only the buyable **and** sampleable product needs it — Shop
     * Now holds the primary slot, so the sample path would otherwise be
     * invisible until the drawer opens. When the primary CTA is already Shop
     * Sample (or there is nothing to sample) the line would just repeat it.
     */
    showSampleLine: boolean;
    /**
     * Give the enquiry its own outlined pill beside the primary. Commerce
     * states need it because `label` is Shop Now / Shop Sample there, and
     * enquiry is still the page's highest-value action — it just doesn't lead.
     * Enquiry-led states already carry the label in the primary slot, so a
     * second pill would duplicate it.
     */
    showSecondaryEnquiry: boolean;
}

/**
 * Resolve the product detail primary CTA label and what it should open, from the
 * product's commerce state. Pure so the routing is unit-testable in isolation.
 */
export const resolveCtaState = ({
    isBuyable,
    isSampleBuyable,
    isMarketplace,
    hasVariantOptions,
    enquiryCTALabel,
    enquiryVisible,
}: CtaStateInput): CtaState => {
    const isEnquiryCTA = !isBuyable && !isSampleBuyable;

    // Transactional states use fixed labels; enquiry-led uses the professional's
    // configured label (e.g. "Request Price / Brochure") falling back to the
    // legacy defaults so nothing breaks when the value is absent.
    const label: CtaLabel = isBuyable
        ? 'Shop Now'
        : isSampleBuyable
          ? 'Shop Sample'
          : (enquiryCTALabel ?? (isMarketplace ? 'Enquire' : 'Request Quote'));

    return {
        label,
        isEnquiryCTA,
        opensEnquiryModalDirectly: isEnquiryCTA && !hasVariantOptions,
        showPrimaryCta: !isEnquiryCTA || enquiryVisible,
        showSampleLine: isBuyable && isSampleBuyable,
        showSecondaryEnquiry: !isEnquiryCTA && enquiryVisible,
    };
};
